import { css } from '@emotion/react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import useUploadFile from 'shared/hooks/use-upload-file';
import { S3NameSpaces } from 'shared/services/s3-upload-factory';
import { NvFilePicker } from 'shared/components/nv-filepicker';
import NvFileUploadProgress from 'shared/components/nv-file-upload-progress';
import NvInlineUploadedFile from 'shared/components/nv-uploaded-file/nv-inline-uploaded-file';
import NvIcon from 'shared/components/nv-icon';
import { config } from '@config/config.json';


// TODO: What should this be?
const MAX_FILE_SIZE = 750 * 1024 * 1024;

const ALLOWED_FILE_TYPES = [
  ...config.files.presentations.split(','),
  ...config.files.documents.split(','),
  ...config.files.pdfs.split(','),
  ...config.files.audios.split(','),
  ...config.files.videos.split(','),
  ...config.files.images.split(','),
  ...config.files.zips.split(','),
  ...config.files.allUploadableExtensions,
];

type RelatedMaterialsSectionProps = {
  setIsRelatedMaterialsUploading: (isUploading: boolean) => void;
};

const RelatedMaterialsSection = ({ setIsRelatedMaterialsUploading }: RelatedMaterialsSectionProps) => {
  const styles = css``;

  const dispatch = useAppDispatch();
  const { watch, setValue } = useFormContext();
  const documents = watch('relatedMaterials');
  const [deletingResourceId, setDeletingResourceId] = useState(null);

  const {
    uploadFiles,
    isUploading,
    hasUploadPercentage,
    filesUploading,
    abortUpload,
  } = useUploadFile();
  const fileUploading = filesUploading[0];

  const onSelectFile = async (file) => {
    setIsRelatedMaterialsUploading(true);
    const [novoedFile] = await uploadFiles([file], S3NameSpaces.ATTACHMENTS);

    // This handles when the user cancels the upload
    if (!novoedFile?.uniqueId) {
      setIsRelatedMaterialsUploading(false);
      return;
    }

    const formattedFile = {
      uniqueId: novoedFile.uniqueId,
      fileName: novoedFile.name,
      fileSize: novoedFile.size,
      fileType: novoedFile.type,
      url: novoedFile.url,
    };
    setValue('relatedMaterials', [...documents, formattedFile], { shouldValidate: true });
    setIsRelatedMaterialsUploading(false);
  };

  const onDeleteFile = (index: number) => {
    const updatedDocuments = documents.filter((_, i) => i !== index);
    setValue('relatedMaterials', updatedDocuments, { shouldValidate: true });
    // TODO: switch to .toSpliced once it is ready
    // setValue('relatedMaterials', documents.toSpliced(index, 1), { shouldValidate: true });
  };

  const onFileError = () => {
    setIsRelatedMaterialsUploading(false);
    dispatch(addAlertMessage({
      type: AlertMessageType.ERROR,
      header: t.FORM.OOPS(),
      message: t.FILE_UPLOAD.NOT_SUPPORTED_ALERT(),
    }));
  };

  let content = (
    <NvFilePicker
      maxSize={MAX_FILE_SIZE}
      multiple={false}
      accept={ALLOWED_FILE_TYPES}
      onChange={([file]) => onSelectFile(file)}
      onSelectError={onFileError}
    >
      <div className='nv-file-upload d-flex justify-content-center align-items-center mt-2'>
        <NvIcon icon='upload' size='small' className='mr-2' />
        <div className='bold color-gray-2'>
          {t.MENTORING_PROGRAMS.GUIDES.CREATE_GUIDE_FLYOUT.UPLOAD_DOCUMENTS()}
        </div>
      </div>
    </NvFilePicker>
  );

  if (isUploading) {
    content = (
      <NvFileUploadProgress
        className='mt-2'
        showFileName={false}
        hasUploadPercentage={hasUploadPercentage}
        uploadInProgress={isUploading}
        fileUploading={fileUploading}
        abortUpload={abortUpload}
      />
    );
  }

  return (
    <div css={styles}>
      <div className='mt-4 semi-bold color-gray-2'>{t.MENTORING_PROGRAMS.GUIDES.CREATE_GUIDE_FLYOUT.RELATED_MATERIALS()}</div>
      <div className='mt-1'>{t.MENTORING_PROGRAMS.GUIDES.CREATE_GUIDE_FLYOUT.UPLOAD_ADDITIONAL_RESOURCES()}</div>
      {content}
      {documents.map((doc, index) => {
        const { fileType, ...rest } = doc;
        return (
          <div className='mt-4'>
            <NvInlineUploadedFile
              resource={{
                contentType: fileType,
                ...rest,
              }}
              onDelete={() => onDeleteFile(index)}
              deletingResourceId={deletingResourceId}
              setDeletingResourceId={setDeletingResourceId}
              deletePopoverText={t.MENTORING_PROGRAMS.GUIDES.CREATE_GUIDE_FLYOUT.DELETE_ATTACHMENT_WARNING()}
              insideDoubleModal
            />
          </div>
        );
      })}
    </div>
  );
};

export default RelatedMaterialsSection;
